import React, { useState, useEffect } from 'react';
import { Briefcase, Save, X } from 'lucide-react';

const EDUCATION_LEVELS = [
  'Primaria',
  'Secundaria',
  'Técnico Superior',
  'Grado Universitario',
  'Post Grado',
  'Maestría',
  'Doctorado'
];

const EXPERIENCE_LEVELS = [
  'Sin experiencia',
  '1-2 años',
  '3-5 años',
  '5-10 años',
  'Más de 10 años'
];

export default function PositionForm({ position, departments, positions, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    nombre: '',
    departamentoId: '',
    reportaA: '', // ID del cargo al que reporta
    proposito: '',
    responsabilidades: '',
    equipamiento: '',
    relacionDepartamentos: [], // Array de IDs de departamentos
    condicionesTrabajo: '',
    // Perfil del cargo
    nivelEducativo: '',
    experienciaRequerida: '',
    conocimientosEspecificos: '',
    habilidadesBlandas: '',
    habilidadesTecnicas: '',
    certificacionesRequeridas: '',
    idiomas: '',
    // Adicionales
    salarioRango: '',
    beneficios: '',
    horario: ''
  });

  useEffect(() => {
    if (position) {
      setFormData({
        ...position,
        // Asegurar que relacionDepartamentos siempre sea un array
        relacionDepartamentos: Array.isArray(position.relacionDepartamentos) 
          ? position.relacionDepartamentos 
          : []
      });
    }
  }, [position]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDepartmentRelationToggle = (deptId) => {
    setFormData(prev => ({
      ...prev,
      relacionDepartamentos: prev.relacionDepartamentos.includes(deptId)
        ? prev.relacionDepartamentos.filter(id => id !== deptId)
        : [...prev.relacionDepartamentos, deptId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      alert('El nombre del cargo es obligatorio');
      return;
    }
    
    if (!formData.departamentoId) {
      alert('Debe seleccionar un departamento');
      return;
    }

    onSave(formData);
  };

  // Filtrar cargos disponibles para reportar (del mismo o diferente departamento)
  const availablePositionsToReport = positions.filter(pos => pos.id !== position?.id);

  // Obtener cargos que reportan a este cargo
  const reportingPositions = positions.filter(pos => pos.reportaA === position?.id);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-blue-100">
            <Briefcase className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800">
            {position ? 'Editar' : 'Nuevo'} Cargo
          </h3>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Información Básica */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            Información Básica
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nombre del Cargo *
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="ej: Gerente de Recursos Humanos"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Departamento *
              </label>
              <select
                name="departamentoId"
                value={formData.departamentoId}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
                required
              >
                <option value="">Seleccionar departamento</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reporta a (Cargo Superior)
              </label>
              <select
                name="reportaA"
                value={formData.reportaA}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              >
                <option value="">Sin reporte directo (Cargo de nivel superior)</option>
                {availablePositionsToReport.map(pos => {
                  const dept = departments.find(d => d.id === pos.departamentoId);
                  return (
                    <option key={pos.id} value={pos.id}>
                      {pos.nombre} ({dept?.nombre || 'Sin dept.'})
                    </option>
                  );
                })}
              </select>
            </div>

            {reportingPositions.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cargos que le reportan
                </label>
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                  <div className="flex flex-wrap gap-2">
                    {reportingPositions.map(pos => {
                      const dept = departments.find(d => d.id === pos.departamentoId);
                      return (
                        <span 
                          key={pos.id}
                          className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm font-semibold"
                        >
                          {pos.nombre} ({dept?.nombre})
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Propósito y Responsabilidades */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            Propósito y Responsabilidades
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Propósito del Cargo
              </label>
              <textarea
                name="proposito"
                value={formData.proposito}
                onChange={handleChange}
                placeholder="Describa el propósito principal de este cargo..."
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Responsabilidades
              </label>
              <textarea
                name="responsabilidades"
                value={formData.responsabilidades}
                onChange={handleChange}
                placeholder="Liste las responsabilidades principales (una por línea)..."
                rows="5"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Consejo: Enumere cada responsabilidad en una línea nueva
              </p>
            </div>
          </div>
        </div>

        {/* Equipamiento y Condiciones */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            Equipamiento y Condiciones de Trabajo
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Equipamiento Necesario
              </label>
              <textarea
                name="equipamiento"
                value={formData.equipamiento}
                onChange={handleChange}
                placeholder="ej: Computadora, software específico, vehículo, herramientas, etc."
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Condiciones de Trabajo
              </label>
              <textarea
                name="condicionesTrabajo"
                value={formData.condicionesTrabajo}
                onChange={handleChange}
                placeholder="ej: Oficina, campo, remoto, viajes, horario flexible, etc."
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Horario
              </label>
              <input
                type="text"
                name="horario"
                value={formData.horario}
                onChange={handleChange}
                placeholder="ej: Lunes a Viernes 8:00 AM - 5:00 PM"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Relación con Otros Departamentos */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            Relación con Otros Departamentos
          </h4>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-700 mb-3">
              Seleccione los departamentos con los que este cargo interactúa:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {departments.map(dept => (
                <label
                  key={dept.id}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.relacionDepartamentos.includes(dept.id)
                      ? 'bg-blue-100 border-blue-400'
                      : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.relacionDepartamentos.includes(dept.id)}
                    onChange={() => handleDepartmentRelationToggle(dept.id)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-semibold">{dept.nombre}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Perfil del Cargo */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            Perfil del Cargo
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nivel Educativo Requerido
              </label>
              <select
                name="nivelEducativo"
                value={formData.nivelEducativo}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              >
                <option value="">Seleccionar nivel</option>
                {EDUCATION_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Experiencia Requerida
              </label>
              <select
                name="experienciaRequerida"
                value={formData.experienciaRequerida}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              >
                <option value="">Seleccionar experiencia</option>
                {EXPERIENCE_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Conocimientos Específicos
              </label>
              <textarea
                name="conocimientosEspecificos"
                value={formData.conocimientosEspecificos}
                onChange={handleChange}
                placeholder="ej: Contabilidad, normativa laboral, gestión de proyectos, etc."
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Habilidades Técnicas
              </label>
              <textarea
                name="habilidadesTecnicas"
                value={formData.habilidadesTecnicas}
                onChange={handleChange}
                placeholder="ej: Excel avanzado, SAP, AutoCAD, programación, etc."
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Habilidades Blandas
              </label>
              <textarea
                name="habilidadesBlandas"
                value={formData.habilidadesBlandas}
                onChange={handleChange}
                placeholder="ej: Liderazgo, trabajo en equipo, comunicación, negociación, etc."
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Certificaciones Requeridas
              </label>
              <input
                type="text"
                name="certificacionesRequeridas"
                value={formData.certificacionesRequeridas}
                onChange={handleChange}
                placeholder="ej: PMP, CPA, Six Sigma, etc."
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Idiomas
              </label>
              <input
                type="text"
                name="idiomas"
                value={formData.idiomas}
                onChange={handleChange}
                placeholder="ej: Español, Inglés (fluido), Francés (básico)"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Compensación y Beneficios */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            Compensación y Beneficios (Opcional)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rango Salarial
              </label>
              <input
                type="text"
                name="salarioRango"
                value={formData.salarioRango}
                onChange={handleChange}
                placeholder="ej: RD$ 35,000 - 45,000"
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Beneficios
              </label>
              <input
                type="text"
                name="beneficios"
                value={formData.beneficios}
                onChange={handleChange}
                placeholder="ej: Seguro médico, bono anual, etc."
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
          <button
            type="submit"
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-green-700 transition-colors"
          >
            <Save className="w-5 h-5" />
            {position ? 'Actualizar' : 'Guardar'} Cargo
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-gray-400 transition-colors"
          >
            <X className="w-5 h-5" />
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
